/**
 * Compact SVG radar (hexagon) showing per-subtopic coverage across the
 * 6 question_types. Each axis is normalized vs `target` (so a full hex
 * = the subtopic has hit its overall coverage_target evenly).
 *
 * Pure SVG, no chart library — keeps the bundle lean.
 */
const TYPES = ["Recognition", "Meaning", "Action", "Risk", "Exception", "CommonMistake"];
const SHORT = { Recognition: "Rec", Meaning: "Mean", Action: "Act", Risk: "Risk", Exception: "Exc", CommonMistake: "CM" };

export default function TypeRadar({ byType = {}, target = 18, size = 96 }) {
  const cx = size / 2;
  const cy = size / 2;
  const r  = size / 2 - 12;

  // Per-type expected = target / 6 (rough). Cap value at 1.0.
  const expectedPerType = Math.max(1, target / 6);
  const values = TYPES.map(t => Math.min(1, (byType[t] || 0) / expectedPerType));

  const point = (i, v) => {
    const angle = (Math.PI * 2 * i) / TYPES.length - Math.PI / 2;
    const rr = r * v;
    return [cx + rr * Math.cos(angle), cy + rr * Math.sin(angle)];
  };
  const ringPoint = (i, frac = 1) => {
    const angle = (Math.PI * 2 * i) / TYPES.length - Math.PI / 2;
    return [cx + r * frac * Math.cos(angle), cy + r * frac * Math.sin(angle)];
  };

  const ring = (frac) =>
    TYPES.map((_, i) => ringPoint(i, frac).join(",")).join(" ");
  const valuePath = values.map((v, i) => point(i, v).join(",")).join(" ");

  const hasAny = Object.values(byType).some(v => v > 0);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label="Question-type coverage radar"
      data-testid="type-radar"
    >
      {/* concentric rings */}
      {[0.33, 0.66, 1].map(f => (
        <polygon
          key={f}
          points={ring(f)}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="1"
        />
      ))}
      {/* axes */}
      {TYPES.map((_, i) => {
        const [x, y] = ringPoint(i, 1);
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={x}
            y2={y}
            stroke="#f3f4f6"
            strokeWidth="1"
          />
        );
      })}
      {/* value polygon */}
      {hasAny && (
        <polygon
          points={valuePath}
          fill="rgba(43, 109, 158, 0.25)"
          stroke="#2B6D9E"
          strokeWidth="1.5"
        />
      )}
      {/* type labels */}
      {TYPES.map((tp, i) => {
        const [x, y] = ringPoint(i, 1.18);
        return (
          <text
            key={tp}
            x={x}
            y={y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9"
            fill="#6b7280"
          >
            {SHORT[tp]}
          </text>
        );
      })}
    </svg>
  );
}
